import { type Type } from "@angular/core";

import { ITranslateObject } from "@app/views-interfaces";
import { SimpleFilter, DynamicFilter } from "./filters";
import type { ApplicationTag } from "@app/views-interfaces";

export type FilterName = string;
export type FilterField<TItem = any> = SimpleFilter<FilterSelectionList, TItem> | DynamicFilter<FilterSelectionList, TItem>;
export type FiltersByField = Map<FilterName, FilterField>;

export type FilterValue = string | number | null;
export type DateFilterValue = number | Date | string;
export type SupportedFilterValue = FilterValue | boolean | Date | FilterValue[];
export type FilterLabel = string | ITranslateObject;

export enum FilterSelectionKind {
    Default = "default",
    Single = "single",
    Multi = "multi",
    Boolean = "boolean",
    Text = "text",
    Date = "date",
}

export type FilterDefaultSelection = { kind: FilterSelectionKind.Default; value: null };
export type FilterSingleSelection = { kind: FilterSelectionKind.Single; value: FilterValue };
export type FilterMultiSelection = { kind: FilterSelectionKind.Multi; values: FilterValue[] };
export type FilterBooleanSelection = { kind: FilterSelectionKind.Boolean; value: boolean };
export type FilterTextSelection = { kind: FilterSelectionKind.Text; value: string };
export type FilterDateSelection = { kind: FilterSelectionKind.Date; value: DateFilterValue | null };
export type FilterSelectionList =
    | FilterDefaultSelection
    | FilterSingleSelection
    | FilterMultiSelection
    | FilterBooleanSelection
    | FilterTextSelection
    | FilterDateSelection;
export type DynamicSelection<TSelection extends FilterSelectionList = FilterSelectionList> = Record<string, TSelection>;

export type FilterSelection = FilterSelectionList | DynamicSelection;

// Anything a filter can be restored from: a persisted selection or a raw value coming from the UI.
export type FilterSelectionInput = FilterSelection | SupportedFilterValue | undefined;

export function createDefaultSelection(kind: FilterSelectionKind.Default): FilterDefaultSelection;
export function createDefaultSelection(kind: FilterSelectionKind.Single): FilterSingleSelection;
export function createDefaultSelection(kind: FilterSelectionKind.Multi): FilterMultiSelection;
export function createDefaultSelection(kind: FilterSelectionKind.Boolean): FilterBooleanSelection;
export function createDefaultSelection(kind: FilterSelectionKind.Text): FilterTextSelection;
export function createDefaultSelection(kind: FilterSelectionKind.Date): FilterDateSelection;
export function createDefaultSelection(kind: FilterSelectionKind): FilterSelectionList;
export function createDefaultSelection(kind: FilterSelectionKind): FilterSelectionList {
    switch (kind) {
        case FilterSelectionKind.Default:
            return { kind, value: null };
        case FilterSelectionKind.Single:
            return { kind, value: "" };
        case FilterSelectionKind.Multi:
            return { kind, values: [] };
        case FilterSelectionKind.Boolean:
            return { kind, value: false };
        case FilterSelectionKind.Text:
            return { kind, value: "" };
        case FilterSelectionKind.Date:
            return { kind, value: null };
    }
}

export type FilterSelections = Record<string, FilterSelection>;
export type Predicate<TItem> = (item: TItem) => boolean;

export interface FilterOption<TValue extends FilterValue = FilterValue> {
    value: TValue;
    label: FilterLabel;
    disabled?: boolean;
}

export interface FilterPill {
    filterName: FilterName;
    value: SupportedFilterValue;
    label: FilterLabel;
}

export interface FilterPillTemplate {
    value: SupportedFilterValue;
    label: FilterLabel;
}

export interface BaseFilterDefinition {
    readonly filterName: FilterName;
    readonly label: FilterLabel;
}

export interface FilterDefinition<TSelection extends FilterSelection> extends BaseFilterDefinition {
    readonly kind: TSelection["kind"];
    readonly defaultSelection: TSelection;
}

export interface StoredColumnFilterState {
    version: 1;
    selections: FilterSelections;
}

export enum FilterViewTypeEnum {
    Summary = "summary",
    Normal = "normal",
}

export type FilterViewType = FilterViewTypeEnum.Summary | FilterViewTypeEnum.Normal;

export type FilterFieldType<TItem = any> = Type<FilterField<TItem>>;

export interface FilterRegistration<TItem = any> {
    service: FilterFieldType<TItem>;

    applications: ApplicationTag[];
    viewTypes: FilterViewType[];
}

export interface ProvideFiltersOptions {
    viewType: FilterViewTypeEnum;
}

export type DynamicFilters<TSelection extends FilterSelectionList, TItem> = Map<FilterName, SimpleFilter<TSelection, TItem>>;
